import { useEffect, useState, MouseEvent, FC } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import { getLoggedUserProfile, logout } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { FeatureFlagGate } from "../FeatureFlagGate";
import { ImpersonationDialog } from "../ImpersonationDialog";
import {
  canImpersonate,
  endImpersonation,
  isImpersonating,
} from "../../utils/impersonation";
import { ADMIN_CONTEXT_ORG_ID_SESSION_KEY } from "../../const";
import { BRAND_COLORS } from "./constants";

interface AppTopbarUserMenuProps {
  className?: string;
}

export const AppTopbarUserMenu: FC<AppTopbarUserMenuProps> = ({
  className,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [initialTenantId, setInitialTenantId] = useState<number | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const userProfile = getLoggedUserProfile();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tenantIdRaw = params.get("impersonationTenantId");
    if (!tenantIdRaw) return;

    // If we arrived from Admin while impersonating, restore the original session
    // before continuing with the dialog flow.
    if (isImpersonating()) {
      endImpersonation();
    }

    if (!canImpersonate()) return;
    const tenantId = tenantIdRaw
      ? Number.parseInt(tenantIdRaw, 10)
      : Number.NaN;

    try {
      sessionStorage.setItem(ADMIN_CONTEXT_ORG_ID_SESSION_KEY, tenantIdRaw);

      if (Number.isFinite(tenantId)) {
        setInitialTenantId(tenantId);
      }
    } catch {
      // Best effort only
    }

    params.delete("from");
    params.delete("openImpersonation");
    params.delete("impersonationTenantId");
    params.delete("adminOrgId");

    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${
      newSearch ? `?${newSearch}` : ""
    }${window.location.hash}`;
    window.history.replaceState({}, "", newUrl);

    setImpersonateDialogOpen(true);
  }, []);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const performLogout = () => {
    handleClose();
    logout();
  };

  const handleCollectionsSettings = () => {
    handleClose();
    navigate("/settings/collections");
  };

  const handleExportsSettings = () => {
    handleClose();
    navigate("/settings/exports");
  };

  const handleImpersonateUser = () => {
    handleClose();
    setImpersonateDialogOpen(true);
  };

  // For testing: use test user if no profile is available
  const testUser = userProfile || {
    email: "john.doe@example.com",
    oktaUserId: "test",
  };

  return (
    <Box className={className}>
      <IconButton
        id="topbar-menu-trigger"
        onClick={handleClick}
        size="small"
        aria-controls={open ? "topbar-user-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="Account"
        sx={{
          padding: "2px",
          "&:hover": {
            backgroundColor: "#f3f4f6",
          },
        }}
      >
        {/* The avatar alone, as the 2026 rebrand mockups draw it: a 38px
            prussian circle. The name is in the menu it opens. */}
        <Box
          sx={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            backgroundColor: BRAND_COLORS.PRUSSIAN,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* lucide's `User` glyph, the same one Admin and Sales Copilot use. */}
          <Box
            component="svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            sx={{ width: "28px", height: "28px", display: "block" }}
          >
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </Box>
        </Box>
      </IconButton>
      <Menu
        id="topbar-user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: {
              marginTop: "4px",
              minWidth: "200px",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            },
          },
          list: {
            sx: {
              paddingTop: "4px",
            },
          },
        }}
      >
        <Box px={2} py={1}>
          <Typography variant="body2" color="text.secondary">
            Signed in as
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {testUser.email}
          </Typography>
        </Box>
        <FeatureFlagGate flagKey="settingsEnabled">
          <Divider />
          <Box py={0.5}>
            <ListSubheader sx={{ fontSize: 12, lineHeight: 1.8 }}>
              SETTINGS
            </ListSubheader>
          </Box>
          <FeatureFlagGate flagKey="collectionsEnabled">
            <MenuItem onClick={handleCollectionsSettings}>
              <ListItemText>Saved Collections</ListItemText>
            </MenuItem>
          </FeatureFlagGate>
          <MenuItem onClick={handleExportsSettings}>
            <ListItemText>Export History</ListItemText>
          </MenuItem>
        </FeatureFlagGate>
        {canImpersonate() && !isImpersonating() && (
          <>
            <Divider />
            <Box py={0.5}>
              <ListSubheader sx={{ fontSize: 12, lineHeight: 1.8 }}>
                DEVELOPER TOOLS
              </ListSubheader>
            </Box>
            <MenuItem
              id="topbar-menu-item-impersonate-user"
              onClick={handleImpersonateUser}
            >
              <ListItemText>Impersonate User</ListItemText>
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem
          id="topbar-menu-item-logout"
          onClick={performLogout}
          sx={{
            color: "#dc2626",
            "&:hover": {
              backgroundColor: "#fef2f2",
            },
          }}
        >
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>

      <ImpersonationDialog
        open={impersonateDialogOpen}
        onClose={() => {
          setImpersonateDialogOpen(false);
          setInitialTenantId(undefined);
        }}
        initialTenantId={initialTenantId}
      />
    </Box>
  );
};
